/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

import { Component, Type } from '@angular/core';

import {
    AviPermissionResource,
    ITechSupport,
    ITechSupportState,
} from 'generated-types';

import { TWindowElement } from 'ajs/modules/data-model/data-model.types';
import { MessageItem, ObjectTypeItem } from 'ajs/modules/data-model/factories';
import { withFullModalMixin } from 'ajs/js/utilities/mixins';
import { TechSupport } from 'object-types';
import { IHttpResponse, IPromise } from 'angular';

export enum TechSupportTypes {
    CLUSTERING = 'clustering',
    DEBUGLOGS = 'debugLogs',
    GSLB = 'gslb',
    METRICSLOGS = 'metricsLogs',
    PLACEMENT = 'placement',
    POOL = 'pool',
    PORTAL = 'portal',
    SERVICEENGINE = 'serviceEngine',
    UPGRADE = 'upgrade',
    VIRTUALSERVICE = 'virtualService',
}

// We are getting tech_support_uuid as response from save api.
interface ITechSupportExtended extends Omit<ITechSupport, 'state'> {
    tech_support_uuid: string;
    state?: MessageItem<ITechSupportState>;
}

interface ITechSupportData {
    config: ITechSupportExtended;
}

export type TTechSupportItem = typeof TechSupportItem;

/**
 * @description Tech Support Item
 * @author Dhanashree Tilak
 */
export class TechSupportItem extends withFullModalMixin(ObjectTypeItem) {
    public getConfig: () => ITechSupportExtended;
    public data: ITechSupportData;

    constructor(args = {}) {
        const extendedArgs = {
            objectName: 'techsupport',
            objectType: TechSupport,
            windowElement: 'lazy-load',
            permissionName: AviPermissionResource.PERMISSION_TECHSUPPORT,
            ...args,
        };

        super(extendedArgs);
    }

    /**
     * @override
     */
    // eslint-disable-next-line no-underscore-dangle
    protected getIdFromData_(data: ITechSupportData): string {
        return data?.config?.uuid ?? data?.config?.tech_support_uuid ?? '';
    }

    /**
     * Import lazy-loaded modal component.
     */
    public async getModalComponent(windowElement: TWindowElement): Promise<Type<Component>> {
        const {
            TechSupportModalComponent,
        } = await import(
            /* webpackChunkName: "tech-support-modal" */
            'ng/lazy-loaded-components/modals/tech-support-modal/tech-support-modal.component'
        );

        return TechSupportModalComponent as Type<Component>;
    }

    /** @override */
    public dataToSave(): ITechSupport {
        const config = super.dataToSave();

        return {
            level: config.level?.toLowerCase(),
            name: config.name,
            description: config.description,
        };
    }

    /** @override */
    protected saveRequest(): IPromise<any> {
        this.busy = true;
        this.errors = null;

        const url = 'api/techsupport';

        const headers: ng.IHttpRequestConfigHeaders = {};

        return this.request<ITechSupport>('post', url, this.dataToSave(), headers)
            .then((value: IHttpResponse<ITechSupport>) => {
                this.busy = false;

                return value;
            })
            .catch(({ data }) => {
                this.busy = false;
                this.errors = data.status;
            });
    }

    /**
     * Return the filename from output.
     */
    public getFileName(): string {
        const { output } = this.getConfig();

        // output may be missing if tech support collection was interrupted.
        if (!output) {
            return '';
        }

        const fileUrl = output.split('/') ?? [];

        return fileUrl[fileUrl.length - 1] ?? '';
    }
}
